/**
 * Base API client for REST endpoints.
 * Requires an API base URL in the runtime config at request time (throws on
 * first use if missing or blank).
 * Attaches Bearer token from the registered token provider when available.
 *
 * The shell registers a token provider at bootstrap via setAuthTokenProvider.
 * The shared-core data layer is platform-neutral and does not know how the
 * shell obtains tokens (Entra on web, something else on future shells).
 *
 * Provider registration (``setAuthTokenProvider`` /
 * ``setExtraHeadersProvider``) lives in the side-effect-free ``./providers``
 * module so callers that only need to register providers (e.g. the
 * scope-override hook) don't pull this module into the import graph at app
 * boot. The functions are re-exported from here so existing consumers keep
 * the same import path.
 *
 * IMPORTANT: ``apiBaseUrl`` is lazy — it reads the runtime config on first
 * access, not at module load. This allows pages that import API modules (which
 * import this client) to be statically loaded in mock mode without crashing,
 * since mock mode has no API base URL.
 */

import { getRuntimeConfig } from "../../config/runtime-config";
import { ApiError } from "./errors";
import { recordServerNow } from "../../domain/current-time";
import { buildRequestHeaders } from "./headers";
import {
  getAuthToken,
  getExtraHeaders,
  notifyRejectedRequest,
  setAuthTokenProvider,
  setExtraHeadersProvider,
} from "./providers";
import { recordApiFailure } from "./recent-errors";
import { joinApiUrl } from "./url";

export {
  ApiError,
  buildRequestHeaders,
  getAuthToken,
  joinApiUrl,
  setAuthTokenProvider,
  setExtraHeadersProvider,
};

let _cachedBaseUrl: string | undefined;

/**
 * Returns the API base URL from the runtime config, on first call.
 * Throws if it is missing — but only when actually called, not at module
 * load time.
 */
export function getApiBaseUrl(): string {
  if (_cachedBaseUrl !== undefined) return _cachedBaseUrl;
  const base = getRuntimeConfig().apiBaseUrl.trim();
  if (!base) {
    throw new Error(
      "Misconfiguration: an API base URL is required but missing or empty. Set apiBaseUrl in the served /config.json (API_BASE_URL in the frontend container's env file), or VITE_API_BASE_URL in .env for a local build (see .env.example).",
    );
  }
  _cachedBaseUrl = base;
  return base;
}

// apiBaseUrl was previously a module-level constant that threw at import
// time when the base URL was absent. Consumers now use getApiBaseUrl()
// instead, keeping the module side-effect-free.

/**
 * Remember a failed call for the feedback panel's "recent errors" (paths only; see `recent-errors.ts`). The trace
 * id is read from `X-Trace-Id` when the API sends it, so Claude can pull the Tempo trace of a reported failure.
 */
function noteFailure(method: string, path: string, res: Response | null): void {
  recordApiFailure({
    method,
    path,
    status: res?.status ?? 0,
    traceId: res?.headers.get("X-Trace-Id") ?? null,
  });
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = joinApiUrl(getApiBaseUrl(), path);
  const token = await getAuthToken();
  const headers = buildRequestHeaders(
    options.headers as Record<string, string> | undefined,
    getExtraHeaders(),
    token,
  );
  const res = await fetch(url, {
    ...options,
    headers,
  }).catch((err: unknown) => {
    // No answer at all (offline, refused, blocked by CORS): status 0.
    noteFailure(options.method ?? "GET", path, null);
    throw err;
  });
  recordServerNow(res.headers.get("X-Server-Now"));
  if (!res.ok) {
    noteFailure(options.method ?? "GET", path, res);
    let detail = "";
    let parsedBody: unknown = null;
    try {
      const body = await res.text();
      if (body) {
        try {
          parsedBody = JSON.parse(body);
          const parsed = parsedBody as { detail?: unknown };
          detail =
            typeof parsed.detail === "string"
              ? parsed.detail
              : parsed.detail !== undefined
                ? JSON.stringify(parsed.detail)
                : body;
        } catch {
          detail = body;
        }
      }
    } catch {
      // body read failed — fall through with no detail
    }
    notifyRejectedRequest({ status: res.status, path, body: parsedBody });
    throw new ApiError(res.status, res.statusText, detail, parsedBody);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),

  /**
   * Authenticated GET for a non-JSON (streamed) body — e.g. an artifact
   * download route. Returns the raw text plus the content-type and the
   * server-suggested filename (from Content-Disposition). Auth is header-based,
   * so this must go through the client (a plain anchor would not carry it).
   */
  getRaw: async (
    path: string,
  ): Promise<{ text: string; contentType: string; filename: string }> => {
    const url = joinApiUrl(getApiBaseUrl(), path);
    const token = await getAuthToken();
    const headers = buildRequestHeaders(undefined, getExtraHeaders(), token);
    const res = await fetch(url, { method: "GET", headers });
    if (!res.ok) {
      noteFailure("GET", path, res);
      const detail = await res.text().catch(() => "");
      notifyRejectedRequest({ status: res.status, path, body: null });
      throw new ApiError(res.status, res.statusText, detail, null);
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return {
      text: await res.text(),
      contentType:
        res.headers.get("content-type") ?? "application/octet-stream",
      filename: match?.[1] ?? path.split("/").pop() ?? "download",
    };
  },

  /**
   * Authenticated GET for a **binary** streamed body — e.g. a PDF the user is
   * about to look at. Distinct from `getRaw`, which decodes as text and would
   * corrupt anything that is not.
   *
   * Auth is header-based here, so a plain `<a href>` or `<iframe src>` pointing
   * at the route would arrive unauthenticated and 401. Callers fetch the blob
   * through this and hand the viewer an object URL instead.
   */
  getBlob: async (
    path: string,
  ): Promise<{ blob: Blob; contentType: string; filename: string }> => {
    const url = joinApiUrl(getApiBaseUrl(), path);
    const token = await getAuthToken();
    const headers = buildRequestHeaders(undefined, getExtraHeaders(), token);
    const res = await fetch(url, { method: "GET", headers });
    if (!res.ok) {
      noteFailure("GET", path, res);
      const detail = await res.text().catch(() => "");
      notifyRejectedRequest({ status: res.status, path, body: null });
      throw new ApiError(res.status, res.statusText, detail, null);
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return {
      blob: await res.blob(),
      contentType:
        res.headers.get("content-type") ?? "application/octet-stream",
      filename: match?.[1] ?? path.split("/").pop() ?? "download",
    };
  },

  /** `options.headers` are per-request header overrides. */
  post: <T>(
    path: string,
    body: unknown,
    options?: { headers?: Record<string, string> },
  ) =>
    request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
      ...(options?.headers ? { headers: options.headers } : {}),
    }),

  /**
   * Authenticated POST whose success body is a file (e.g. a CSV the action
   * also records the download of). Returns the text plus the
   * server-suggested filename. Errors carry the parsed JSON detail like
   * `request`, so a refusal's message reaches the page.
   */
  postRaw: async (
    path: string,
    body: unknown,
  ): Promise<{ text: string; contentType: string; filename: string }> => {
    const url = joinApiUrl(getApiBaseUrl(), path);
    const token = await getAuthToken();
    const headers = buildRequestHeaders(undefined, getExtraHeaders(), token);
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    recordServerNow(res.headers.get("X-Server-Now"));
    if (!res.ok) {
      noteFailure("POST", path, res);
      const bodyText = await res.text().catch(() => "");
      let parsedBody: unknown = null;
      let detail = bodyText;
      try {
        parsedBody = bodyText ? JSON.parse(bodyText) : null;
        const parsed = parsedBody as { detail?: unknown } | null;
        if (parsed && parsed.detail !== undefined) {
          detail =
            typeof parsed.detail === "string"
              ? parsed.detail
              : JSON.stringify(parsed.detail);
        }
      } catch {
        // not JSON — keep the raw text as the detail
      }
      notifyRejectedRequest({ status: res.status, path, body: parsedBody });
      throw new ApiError(res.status, res.statusText, detail, parsedBody);
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return {
      text: await res.text(),
      contentType:
        res.headers.get("content-type") ?? "application/octet-stream",
      filename: match?.[1] ?? path.split("/").pop() ?? "download",
    };
  },

  /**
   * POST with a FormData body (multipart/form-data). Does NOT set
   * Content-Type — the browser inserts the correct multipart boundary.
   */
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const url = joinApiUrl(getApiBaseUrl(), path);
    const token = await getAuthToken();
    // Intentionally omit Content-Type so fetch adds multipart/form-data + boundary.
    const authHeaders: Record<string, string> = {};
    if (token) authHeaders["Authorization"] = `Bearer ${token}`;
    const extraHeaders = getExtraHeaders();
    const headers = { ...extraHeaders, ...authHeaders };
    const res = await fetch(url, { method: "POST", headers, body: form });
    if (!res.ok) {
      noteFailure("POST", path, res);
      let detail = "";
      let parsedBody: unknown = null;
      try {
        const bodyText = await res.text();
        if (bodyText) {
          try {
            parsedBody = JSON.parse(bodyText);
            const parsed = parsedBody as { detail?: unknown };
            detail =
              typeof parsed.detail === "string"
                ? parsed.detail
                : JSON.stringify(parsed.detail ?? bodyText);
          } catch {
            detail = bodyText;
          }
        }
      } catch {
        /* ignore */
      }
      notifyRejectedRequest({ status: res.status, path, body: parsedBody });
      throw new ApiError(res.status, res.statusText, detail, parsedBody);
    }
    return res.json() as Promise<T>;
  },

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),

  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),

  /**
   * A JSON request with any write method, abortable: the object UI's command
   * calls, whose method comes from the manifest and whose dry runs are
   * superseded as the person types.
   */
  send: <T>(
    method: "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body: unknown,
    options?: { signal?: AbortSignal },
  ) =>
    request<T>(path, {
      method,
      body: JSON.stringify(body),
      ...(options?.signal ? { signal: options.signal } : {}),
    }),
};
